import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Skeleton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Gauge, AlertTriangle, CheckCircle, Calendar, CalendarDays } from 'lucide-react';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isBetween);

import { bandwidthService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { UserBandwidth } from '@/types';

type TimeRange = 'today' | 'week';

/**
 * BandwidthWidget
 * Shows user's workload capacity vs allocation
 * Prevents overloading and enables capacity planning
 */
const BandwidthWidget: React.FC = () => {
  const [timeRange, setTimeRange] = useState<TimeRange>('today');
  const today = dayjs().format('YYYY-MM-DD');

  // Fetch bandwidth data from backend
  const { data: bandwidthResponse, isLoading } = useQuery({
    queryKey: QUERY_KEYS.myBandwidth(today),
    queryFn: () => bandwidthService.getMyBandwidth(today),
    staleTime: 30000, // 30 seconds
  });

  const bandwidth = bandwidthResponse?.data;

  const totalHours = bandwidth?.totalAvailableHours ?? 8;
  const allocatedHours = bandwidth?.allocatedHours ?? 0;
  const loggedHours = bandwidth?.loggedHours ?? 0;
  const freeHours = bandwidth?.freeHours ?? 0;
  const utilizationPercent = bandwidth?.utilizationPercentage ?? 0;
  const isOverloaded = bandwidth?.isOverloaded ?? false;


  // Calculate day-wise distribution for weekly view (currently not used - week view uses today's data)
  // TODO: Implement weekly bandwidth API endpoint for proper week view support
  const weekDistribution: Array<{
    day: string;
    date: string;
    isToday: boolean;
    isWeekend: boolean;
    allocated: number;
    logged: number;
    available: number;
    utilization: number;
  }> = [];

  // Determine status
  const getStatus = () => {
    if (utilizationPercent > 100) return { color: 'error', label: 'Overloaded' };
    if (utilizationPercent > 85) return { color: 'warning', label: 'Near Capacity' };
    if (utilizationPercent > 50) return { color: 'primary', label: 'Optimal' };
    return { color: 'success', label: 'Available' };
  };

  const status = getStatus();

  if (isLoading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width={150} height={28} />
          <Skeleton variant="rounded" height={100} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Gauge size={20} style={{ color: '#64748b' }} />
            <Typography variant="h6" fontWeight={600}>
              Bandwidth
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              bgcolor: `${status.color}.50`,
              borderRadius: '12px',
            }}
          >
            {isOverloaded ? (
              <AlertTriangle size={14} style={{ color: '#dc2626' }} />
            ) : (
              <CheckCircle size={14} style={{ color: status.color === 'success' ? '#22c55e' : '#3b82f6' }} />
            )}
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: `${status.color}.700` }}
            >
              {status.label}
            </Typography>
          </Box>
        </Box>

        {/* Time Range Toggle */}
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            value={timeRange}
            exclusive
            onChange={(_, newValue) => newValue && setTimeRange(newValue)}
            size="small"
            fullWidth
            sx={{
              '& .MuiToggleButton-root': {
                py: 0.5,
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
              },
            }}
          >
            <ToggleButton value="today" sx={{ gap: 0.5 }}>
              <Calendar size={16} />
              Today
            </ToggleButton>
            <ToggleButton value="week" sx={{ gap: 0.5 }}>
              <CalendarDays size={16} />
              This Week
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Utilization Display */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Utilization
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {utilizationPercent}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(utilizationPercent, 100)}
            color={status.color as 'primary' | 'success' | 'warning' | 'error'}
            sx={{
              height: 10,
              borderRadius: 5,
              bgcolor: 'grey.100',
              '& .MuiLinearProgress-bar': {
                borderRadius: 5,
              },
            }}
          />
        </Box>

        {/* Hours Breakdown */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
          <Tooltip title={timeRange === 'today' ? 'Hours logged today' : 'Hours logged this week'}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={600} color="primary.main">
                {loggedHours.toFixed(1)}h
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Logged
              </Typography>
            </Box>
          </Tooltip>

          <Tooltip title={timeRange === 'today' ? 'Hours allocated to tasks today' : 'Hours allocated to tasks this week'}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={600} color="text.primary">
                {allocatedHours.toFixed(1)}h
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Allocated
              </Typography>
            </Box>
          </Tooltip>

          <Tooltip title={timeRange === 'today' ? 'Free hours available today' : 'Free hours available this week'}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h5"
                fontWeight={600}
                color={freeHours > 0 ? 'success.main' : 'error.main'}
              >
                {freeHours.toFixed(1)}h
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Free
              </Typography>
            </Box>
          </Tooltip>
        </Box>

        {/* Warning for overload */}
        {isOverloaded && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: 'error.50',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <AlertTriangle size={16} style={{ color: '#dc2626' }} />
            <Typography variant="body2" color="error.main">
              You're overallocated by {(allocatedHours - totalHours).toFixed(1)}h
            </Typography>
          </Box>
        )}

        {/* Day-wise Distribution - Only show for weekly view */}
        {timeRange === 'week' && weekDistribution.length > 0 && (
          <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Daily Breakdown
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
              {weekDistribution.map((day) => (
                <Box
                  key={day.day}
                  sx={{
                    flex: 1,
                    textAlign: 'center',
                    opacity: day.isWeekend ? 0.5 : 1,
                  }}
                >
                  {/* Day name and date */}
                  <Typography
                    variant="caption"
                    fontWeight={day.isToday ? 700 : 500}
                    sx={{
                      display: 'block',
                      mb: 0.5,
                      color: day.isToday ? 'primary.main' : 'text.secondary',
                    }}
                  >
                    {day.day}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 1,
                      fontSize: '0.65rem',
                      color: 'text.disabled',
                    }}
                  >
                    {day.date}
                  </Typography>

                  {/* Visual bar */}
                  <Tooltip
                    title={
                      day.isWeekend
                        ? 'Weekend'
                        : `Allocated: ${day.allocated}h / Logged: ${day.logged}h`
                    }
                  >
                    <Box
                      sx={{
                        height: 60,
                        width: '100%',
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        position: 'relative',
                        overflow: 'hidden',
                        mb: 0.5,
                      }}
                    >
                      {/* Allocated hours bar */}
                      {!day.isWeekend && day.allocated > 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: `${Math.min((day.allocated / 8) * 100, 100)}%`,
                            bgcolor:
                              day.utilization > 100
                                ? 'error.light'
                                : day.utilization > 85
                                ? 'warning.light'
                                : day.utilization > 50
                                ? 'primary.light'
                                : 'success.light',
                            transition: 'all 0.3s ease',
                          }}
                        />
                      )}
                      {/* Logged hours indicator */}
                      {!day.isWeekend && day.logged > 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '3px',
                            height: `${Math.min((day.logged / 8) * 100, 100)}%`,
                            bgcolor: 'primary.dark',
                            zIndex: 1,
                          }}
                        />
                      )}
                    </Box>
                  </Tooltip>

                  {/* Hours label */}
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: day.isWeekend
                        ? 'text.disabled'
                        : day.utilization > 100
                        ? 'error.main'
                        : 'text.primary',
                    }}
                  >
                    {day.isWeekend ? '—' : `${day.allocated}h`}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Legend */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mt: 2,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: 'primary.light',
                    borderRadius: 0.5,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Allocated
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 3,
                    height: 12,
                    bgcolor: 'primary.dark',
                    borderRadius: 0.5,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Logged
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BandwidthWidget;
